import React, { useState, useEffect } from 'react'

import Heading from 'presentation/shared/components/Heading'

import * as S from './styles'
import SupportText from 'presentation/shared/components/SupportText'
import OpmeTable from 'presentation/hospital/components/OpmeTable'
import TussTable from 'presentation/hospital/components/TussTable'
import { Cid } from 'domain/entities/cid'
import { Tuss } from 'domain/entities/tuss'
import ActualPage from 'presentation/shared/components/ActualPage'
import { useFormikContext } from 'formik'
import { AddSurgicalOrderFormValues } from '../index'
import { Profile } from 'common/enum/profile'
import { DocumentItem } from 'presentation/shared/components/DocumentsArea/DocumentsAreaModal'
import DocumentsArea from 'presentation/shared/components/DocumentsArea'
import FavoriteButton from 'presentation/shared/components/FavoriteButton'
import Modal from 'presentation/shared/components/Modal'
import { makeLocalStorageAdapter } from 'main/factories/cache/local-storage-adapter-factory'
import { getDaysLeftToDate } from '../../../../../../common/utils/getDaysLeftToDate'
import { ChipNew } from 'presentation/patient/components/Forms/SchedulingNpsForm/styles'
import TextFieldNew from 'presentation/shared/components/TextFieldNew'
import SupportTextNew from 'presentation/shared/components/SupportTextNew'
import TextAreaNew from 'presentation/shared/components/TextAreaNew'
import ButtonNew from 'presentation/shared/components/ButtonNew'
import moment from 'moment'

type Props = {
  cidList?: Cid[]
  tussList?: Tuss[]
  submit?: () => void
  goBack?: () => void
  onlyVisualization?: boolean
  addFavorite?: (value: string) => Promise<void>
  profile?: Profile
  updateOrder?: boolean
  defaultOpme?: boolean
  disabled?: boolean
}

export function SelectSurgeryInfo({
  submit,
  cidList,
  tussList,
  goBack,
  onlyVisualization,
  addFavorite,
  profile,
  updateOrder,
  defaultOpme,
  disabled
}: Props) {
  const { values, handleChange, setFieldValue, isValid, errors } =
    useFormikContext<AddSurgicalOrderFormValues>()
  const [showOpme, setShowOpme] = useState<boolean | undefined>(defaultOpme)
  const [documents, setDocuments] = useState([] as DocumentItem[])
  const [favoriteModal, setFavoriteModal] = useState(false)
  const [showElectiveModal, setShowElectiveModal] = useState<boolean>(false)

  const handleElectiveModalClose = () => {
    setShowElectiveModal(false)
    setFieldValue('hospitalizationType', 'elective')
    setFieldValue('expectedDate', '')
  }

  const checkUrgencyDate = (
    hospitalizationType: string,
    expectedDate: string
  ) => {
    if (hospitalizationType === 'urgency' && expectedDate) {
      const urgencyDate = moment(expectedDate, 'YYYY-MM-DD').startOf('day')
      const today = moment().startOf('day')
      if (urgencyDate.diff(today, 'days') > 5) {
        setShowElectiveModal(true)
      }
    }
  }

  useEffect(() => {
    setShowOpme(defaultOpme)
  }, [defaultOpme])

  const handleSubmit = () => {
    if (!showOpme) {
      values.opmes = []
    }
    if (submit) submit()
    makeLocalStorageAdapter().set('favorite', {})
  }

  const handleOpmeSelected = (value: boolean) => {
    setShowOpme(value)
    setFieldValue('use_opme', value)
  }

  const handleChangeDocuments = (documents: DocumentItem[]) => {
    setFieldValue('documents_attached', documents)
    setDocuments(documents)
  }

  const handleFavoriteModal = (value: string) => {
    if (addFavorite) {
      addFavorite(value)
    }
    setFavoriteModal(false)
  }

  const handleFavoriteOpme = (
    values: AddSurgicalOrderFormValues[],
    type: string
  ) => {
    const favoriteOrder = makeLocalStorageAdapter().get('favorite')
    if (favoriteOrder && Object.keys(favoriteOrder).length > 0) {
      if (type === 'opme') {
        return values.length > 0 ? values : favoriteOrder?.opme.solicitations
      }

      if (type === 'providers') {
        return favoriteOrder?.opme.providers
      }
    }
    return values
  }

  const hospitalRegimesByProfile = () => {
    return (
      hospitalRegimes[profile as keyof typeof hospitalRegimes] ??
      hospitalRegimes['regimes']
    )
  }

  const renderOpmeInput = (
    <S.ChipContainer>
      <label>
        {profile === Profile.CRMO ? 'OPME' : 'Procedimento utilizará OPME?'}
        <span>*</span>
      </label>
      <div>
        {hasOpme.map((opme) => (
          <ChipNew
            key={opme.label}
            label={opme.label}
            customValue={opme.value}
            disabled={false}
            name="opme"
            checked={opme.value === showOpme}
            onCheck={() => handleOpmeSelected(opme.value)}
            dataTestId={`use-opme-chip-${opme.value}`}
          />
        ))}
      </div>
    </S.ChipContainer>
  )

  const extraBlood = (
    <S.ChipContainer>
      <label>
        {profile === Profile.CRMO ? 'Banco de sangue?' : 'Reserva de sangue'}{' '}
      </label>
      <div>
        {extraBloods.map((extraBlood) => (
          <ChipNew
            name="extraBlood"
            id={'extraBlood-' + extraBlood.value}
            key={'extra-blood-' + extraBlood.value}
            label={extraBlood.label}
            value={String(extraBlood.value)}
            disabled={onlyVisualization}
            onCheck={(val) => setFieldValue('extraBlood', val === 'true')}
            checked={extraBlood.value === values.extraBlood}
            dataTestId={`extra-blood-chip-${extraBlood.value}`}
          />
        ))}
      </div>
    </S.ChipContainer>
  )

  useEffect(() => {
    setDocuments(values?.documents_attached)
  }, [])

  useEffect(() => {
    function changeRequestDailiesByHospitalizationMode() {
      if (values.hospitalizationMode === 'day hospital') {
        setFieldValue('requestedDailies', 1)
      }
      if (values.hospitalizationMode === 'ambulatory') {
        setFieldValue('requestedDailies', 0)
      }
    }
    changeRequestDailiesByHospitalizationMode()
  }, [values.hospitalizationMode])

  useEffect(() => {
    const favoriteOpme = makeLocalStorageAdapter().get('favorite')
    if (favoriteOpme && Object.keys(favoriteOpme).length > 0) {
      handleOpmeSelected(true)
    }
  }, [])

  return (
    <S.Wrapper>
      {!updateOrder && (
        <>
          <ActualPage text="Voltar" onClick={goBack} />
          <Heading size="xlarge" color="primary" as="h1">
            Novo pedido cirúrgico
          </Heading>
        </>
      )}
      <div style={{ display: 'flex' }}>
        <Heading
          size="large"
          style={{ marginTop: '1em', marginBottom: '0.50em', flex: 1 }}
        >
          Procedimento
        </Heading>
        {!!addFavorite && (
          <FavoriteButton
            data-testid="btn-procedure-favorite"
            onClick={() => setFavoriteModal(!favoriteModal)}
            disabled={onlyVisualization}
          />
        )}
      </div>
      <Modal
        title="Nome do favorito"
        description="Favorite um procedimento para agilizar a construção dos próximos pedidos cirúrgicos"
        show={favoriteModal}
        preventAutomateClose
        type="favorite"
        hasInput={(value) => handleFavoriteModal(value)}
        bgZIndex={12}
        actionLabel="Salvar"
        close={() => setFavoriteModal(!favoriteModal)}
        subtitleProps={{
          colorFont: 'black'
        }}
        style={{ padding: '24px', width: 400 }}
      />
      <Modal
        title="Pedido enquadrado como Eletivo"
        show={showElectiveModal}
        close={handleElectiveModalClose}
        preventAutomateClose
        style={{
          height: 'auto',
          width: '100%',
          maxWidth: '500px',
          position: 'relative'
        }}
      >
        <div style={{ textAlign: 'left' }}>
          <Heading>Pedido enquadrado como Eletivo</Heading>
          <SupportTextNew style={{ marginTop: '16px' }}>
            Pedido com data superior a 5 dias será enquadrado como eletivo e
            seguirá processo de autorização da operadora com prazo de até 21
            dias.
          </SupportTextNew>
          <SupportTextNew style={{ marginTop: '16px' }}>
            A data da cirurgia deverá ser definida posteriormente no fluxo de
            Agendamento Cirúrgico.
          </SupportTextNew>
          <div style={{ width: '100%', marginTop: '32px' }}>
            <ButtonNew
              size="large"
              type="button"
              fullWidth
              onClick={handleElectiveModalClose}
            >
              Entendi
            </ButtonNew>
          </div>
        </div>
      </Modal>
      <TussTable
        tussList={tussList}
        cidList={cidList}
        doctorName={values.doctor?.name}
        onlyVisualization={onlyVisualization}
        cid={values.cids}
        setCid={(cid) => setFieldValue('cids', cid)}
        selectedProcedures={values.procedures}
        selectedCids={values.cids}
        setSelectedProcedures={(procedure) =>
          setFieldValue('procedures', procedure)
        }
        setSelectedCids={(cid) => setFieldValue('cids', cid)}
      />
      <Heading size="large" style={{ marginTop: '32px' }}>
        Cirurgia
      </Heading>
      {profile === Profile.CRMO && (
        <S.ChipContainer style={{ flexDirection: 'row', marginBottom: '1rem' }}>
          <label style={{ marginRight: '1rem', lineHeight: '50px' }}>
            Banco de sangue?<span>*</span>
          </label>
          <div>
            {extraBloods.map((extraBlood) => (
              <ChipNew
                style={{ marginTop: '0' }}
                key={extraBlood.label + 'extraBlood'}
                label={extraBlood.label}
                value={String(extraBlood.value)}
                name="extraBlood"
                id={'extraBlood-' + extraBlood.value}
                disabled={onlyVisualization}
                onCheck={(val) => setFieldValue('extraBlood', val === 'true')}
                checked={extraBlood.value === values.extraBlood}
                dataTestId={`extra-blood-chip-${extraBlood.value}`}
              />
            ))}
          </div>
        </S.ChipContainer>
      )}

      <S.SurgeryWrapper>
        <S.ChipContainer>
          <label>
            Técnica<span>*</span>
          </label>
          <div>
            {techniques.map((technique) => (
              <ChipNew
                key={technique.label}
                dataTestId={technique.value}
                label={technique.label}
                customValue={technique.value}
                onCheck={() =>
                  setFieldValue(
                    'technique',
                    technique.value === values.technique
                      ? undefined
                      : technique.value
                  )
                }
                name="technique"
                disabled={onlyVisualization}
                checked={values.technique === technique.value}
                id={'technique-' + technique.value}
              />
            ))}
          </div>
        </S.ChipContainer>
        <S.ChipContainer>
          <label>Congelação</label>
          <div>
            {freezing.map((freezing) => (
              <ChipNew
                key={freezing.label}
                dataTestId={`${freezing.value}`}
                label={freezing.label}
                customValue={freezing.value}
                disabled={onlyVisualization}
                name="freeze"
                onCheck={() =>
                  setFieldValue(
                    'freeze',
                    freezing.value === values.freeze
                      ? undefined
                      : freezing.value
                  )
                }
                checked={values.freeze === freezing.value}
                id={'freeze-' + freezing.value}
              />
            ))}
          </div>
        </S.ChipContainer>
        <S.ChipContainer>
          <label>Uso CTI</label>
          <div>
            {ctis.map((cti) => (
              <ChipNew
                key={cti.label}
                dataTestId={`cti-${cti.value}`}
                label={cti.label}
                customValue={cti.value}
                disabled={onlyVisualization}
                name="cti"
                onCheck={() =>
                  setFieldValue(
                    'cti',
                    cti.value === values.cti ? undefined : cti.value
                  )
                }
                checked={values.cti === cti.value}
                id={'cti-' + cti.value}
              />
            ))}
            <SupportText color="lightGray" style={{ marginTop: '8px' }}>
              Após a conclusão do pedido, o CTI será notificado da sua reserva
            </SupportText>
          </div>
        </S.ChipContainer>
        <S.ChipContainer>
          <label>
            Alergias<span>*</span>
          </label>
          <div>
            {allergies.map((allergy) => (
              <ChipNew
                key={allergy.label}
                dataTestId={`allergy-chip-${allergy.value}`}
                label={allergy.label}
                value={allergy.value}
                disabled={onlyVisualization}
                onCheck={handleChange('allergy')}
                name="allergy"
                checked={values.allergy === allergy.value}
              />
            ))}
          </div>
          <div>
            <TextFieldNew
              disabled={onlyVisualization}
              placeholder="Outras alergias"
              onChange={handleChange('otherAllergies')}
              defaultValue={values.otherAllergies}
              data-testid={`other-allergy-input`}
            />
          </div>
        </S.ChipContainer>
        {profile === Profile.SECRETARY && extraBlood}

        {profile === Profile.CRMO && renderOpmeInput}
      </S.SurgeryWrapper>
      {profile === Profile.SECRETARY && (
        <S.ChipContainer style={{ marginTop: '40px' }}>
          <label>
            Transfusão sanguínea<span>*</span>
          </label>
          <div>
            {bloodTransfusion.map((transfusion) => {
              return (
                <ChipNew
                  key={transfusion.label}
                  dataTestId={`chip-transfusion-${transfusion.value}`}
                  label={transfusion.label}
                  customValue={transfusion.value}
                  disabled={onlyVisualization}
                  name="bloodTransfusion"
                  checked={transfusion.value === values.bloodTransfusion}
                  onCheck={async (val) =>
                    await setFieldValue('bloodTransfusion', val)
                  }
                />
              )
            })}
          </div>
        </S.ChipContainer>
      )}
      {profile === Profile.SECRETARY && (
        <>
          <Heading
            size="large"
            style={{ marginTop: '60px', marginBottom: '10px' }}
          >
            OPME
          </Heading>
          {renderOpmeInput}
        </>
      )}
      {showOpme && (
        <S.OpmeWrapper>
          <Heading size="large">OPME</Heading>
          <OpmeTable
            selectedOpme={handleFavoriteOpme(values.opmes, 'opme')}
            setSelectedOpme={(opme) => setFieldValue('opmes', opme)}
            handleChange={(val) => setFieldValue('providers', val)}
            onlyVisualization={false}
            defaultProviders={values.providers}
          />
        </S.OpmeWrapper>
      )}
      <S.HospitalizationWrapper>
        <Heading size="large" style={{ marginBottom: '16px' }} required>
          Internação
        </Heading>

        <div>
          <S.ChipContainer>
            <label>
              Caráter<span>*</span>
            </label>
            <div>
              {characters.map((character) => (
                <ChipNew
                  key={character.label}
                  label={character.label}
                  value={character.value}
                  name="hospitalizationType"
                  disabled={onlyVisualization}
                  onCheck={(val) => {
                    handleChange('hospitalizationType')(val)
                    checkUrgencyDate(val, values.expectedDate ?? '')
                  }}
                  checked={values.hospitalizationType === character.value}
                  dataTestId={`hospitalization-type-chip-${character.value}`}
                />
              ))}
            </div>
            {!!values.hospitalizationType &&
              (values.hospitalizationType === 'urgency' ? (
                <div>
                  <SupportTextNew
                    color="neutral400"
                    style={{ marginTop: '8px' }}
                  >
                    Urgência 72 horas a partir da data do pedido
                  </SupportTextNew>
                  <div style={{ maxWidth: '600px', marginTop: '16px' }}>
                    <SupportTextNew
                      color="neutral400"
                      size="xsmall"
                      style={{ lineHeight: 1.4 }}
                    >
                      **Declaro estar ciente de que a senha será solicitada com
                      o paciente no local. Após 7 dias se procedimento não
                      realizado o pedido será automaticamente cancelado.
                    </SupportTextNew>
                  </div>
                </div>
              ) : (
                <div style={{ maxWidth: '600px', marginTop: '16px' }}>
                  <SupportTextNew
                    color="neutral400"
                    size="xsmall"
                    style={{ lineHeight: 1.4 }}
                  >
                    **Declaro estar ciente de que este pedido será enviado a
                    operadora e o prazo de resposta é de até 21 dias.
                  </SupportTextNew>
                </div>
              ))}
          </S.ChipContainer>
        </div>

        <div>
          <S.ChipContainer>
            <label>
              {values.hospitalizationType === 'urgency'
                ? 'Data da Urgência'
                : 'Data prevista para cirurgia'}
              {values.hospitalizationType === 'urgency' ? <span>*</span> : ''}
            </label>
            <TextFieldNew
              type="date"
              name="expectedDate"
              id="expectedDate"
              onChange={handleChange}
              onInputChange={(val) =>
                checkUrgencyDate(values.hospitalizationType, val)
              }
              disabled={onlyVisualization}
              value={values.expectedDate ?? ''}
              error={errors?.expectedDate || undefined}
              required={values.hospitalizationType === 'urgency'}
              data-testid="expected-date-procedure-input"
            />
            {values.hospitalizationType === 'urgency' &&
              values.expectedDate &&
              !errors?.expectedDate && (
                <S.DateSuportText>
                  Dias até a urgência:&nbsp;
                  {values.expectedDate
                    ? getDaysLeftToDate(new Date(values.expectedDate))
                    : 0}
                  &nbsp;dias
                </S.DateSuportText>
              )}
          </S.ChipContainer>
          <S.ChipContainer>
            <label>
              Paciente está internado:{' '}
              {values.patientHospitalized ? 'Sim' : 'Não'}
            </label>
            {values.patientHospitalized && values.patient?.internation && (
              <S.InlineInfo>
                <div>
                  <SupportTextNew weight="normal">
                    Nº atendimento:
                  </SupportTextNew>
                  <SupportTextNew size="large" weight="bold">
                    {values.patient.internation.cd_atendimento || '-'}
                  </SupportTextNew>
                </div>
                <div>
                  <SupportTextNew weight="normal">
                    Local internação:
                  </SupportTextNew>
                  <SupportTextNew size="large" weight="bold">
                    {values.patient.internation.ds_leito || '-'}
                  </SupportTextNew>
                </div>
              </S.InlineInfo>
            )}
          </S.ChipContainer>
        </div>
        <div>
          <S.ChipContainer style={{ width: '100%' }}>
            <label>
              Regime Hospitalar<span>*</span>
            </label>
            <div>
              {hospitalRegimesByProfile().map((regime) => (
                <ChipNew
                  key={regime.label}
                  label={regime.label}
                  value={regime.value}
                  name="hospitalizationMode"
                  onCheck={handleChange('hospitalizationMode')}
                  disabled={onlyVisualization}
                  checked={values.hospitalizationMode === regime.value}
                  dataTestId={`hospital-regimes-chip-${regime.value}`}
                />
              ))}
            </div>
          </S.ChipContainer>
          {(profile === Profile.SECRETARY || profile === Profile.CRMO) && (
            <S.DiaryWrapper>
              <label>
                Quantidade de diárias
                {values.hospitalizationMode === 'ambulatory' ? (
                  ''
                ) : (
                  <span>*</span>
                )}
              </label>
              <TextFieldNew
                type="number"
                style={{ width: '100%', maxWidth: '54px' }}
                value={values.requestedDailies}
                name="requestedDailies"
                id="requestedDailies"
                onChange={handleChange}
                min={0}
                disabled={
                  onlyVisualization ||
                  values.hospitalizationMode === 'ambulatory' ||
                  values.hospitalizationMode === 'day hospital'
                }
                data-testid={`requested-dailies-input`}
              />
            </S.DiaryWrapper>
          )}
        </div>
      </S.HospitalizationWrapper>
      <S.ChipContainer style={{ marginTop: '20px' }}>
        <label>
          História Clínica<span>*</span>
        </label>
        <div>
          <TextAreaNew
            style={{ width: '100%' }}
            rows={5}
            onChange={handleChange('clinicalHistory')}
            value={values.clinicalHistory}
            disabled={onlyVisualization}
            dataTestId={`clinical-history-input`}
          />
        </div>
      </S.ChipContainer>
      <S.ChipContainer style={{ marginTop: '20px' }}>
        <label>Observações</label>
        <div>
          <TextAreaNew
            style={{ width: '100%' }}
            rows={5}
            onChange={handleChange('observations')}
            value={values.observations}
            disabled={onlyVisualization}
            dataTestId={`observations-input`}
          />
        </div>
      </S.ChipContainer>

      {!updateOrder && (
        <DocumentsArea
          defaultDocuments={documents}
          handleChangeDocuments={(documents: DocumentItem[]) =>
            handleChangeDocuments(documents)
          }
          canSee={values.canSee}
          preventCreateExam={false}
        />
      )}

      <S.Buttons>
        {!!addFavorite && (
          <FavoriteButton
            data-testid="btn-procedure-favorite"
            onClick={() => setFavoriteModal(!favoriteModal)}
            disabled={!isValid || (showOpme && values.opmes?.length === 0)}
          />
        )}

        <ButtonNew
          size="large"
          onClick={onlyVisualization ? goBack : handleSubmit}
          type="button"
          disabled={
            onlyVisualization
              ? false
              : values.hospitalizationType === 'urgency'
              ? !(
                  isValid &&
                  values.expectedDate !== undefined &&
                  values.expectedDate !== ''
                )
              : !isValid
          }
          data-testid={`go-back-or-next-button`}
          style={{
            width: '100%',
            maxWidth: '320px'
          }}
        >
          {onlyVisualization ? 'Voltar' : 'Enviar'}
        </ButtonNew>

        {updateOrder && (
          <ButtonNew
            size="large"
            onClick={handleSubmit}
            type="button"
            disabled={disabled || !isValid}
            data-testid={`save-button`}
            style={{
              width: '100%',
              maxWidth: '320px'
            }}
          >
            Salvar
          </ButtonNew>
        )}
      </S.Buttons>
    </S.Wrapper>
  )
}

export default SelectSurgeryInfo

const techniques = [
  {
    label: 'Convencional',
    value: 'conventional'
  },
  {
    label: 'Vídeo',
    value: 'video'
  },
  {
    label: 'Robótica',
    value: 'robotic'
  },
  {
    label: 'Robô Mako',
    value: 'makoRobot'
  },
  {
    label: 'Robô Rosa',
    value: 'rosaRobot'
  },
  {
    label: 'Laser',
    value: 'laser'
  }
]

const ctis = [
  {
    label: 'Precisa de CTI',
    value: true
  },
  {
    label: 'Não precisa de CTI',
    value: false
  }
]

const freezing = [
  {
    label: 'Sim',
    value: true
  },
  {
    label: 'Não',
    value: false
  }
]

const hasOpme = [
  {
    label: 'Uso de OPME',
    value: true
  },
  {
    label: 'Não será utilizado OPME',
    value: false
  }
]

const allergies = [
  {
    label: 'Alérgico a látex',
    value: 'Látex'
  },
  {
    label: 'Não alérgico a látex',
    value: 'false'
  },
  {
    label: 'Não informado',
    value: 'latex-not-informed'
  }
]

const hospitalRegimes = {
  regimes: [
    {
      label: 'Hospitalar',
      value: 'hospital'
    },
    {
      label: 'Hospital dia',
      value: 'day hospital'
    },
    {
      label: 'Ambulatorial',
      value: 'ambulatory'
    }
  ],
  patient: [],
  doctor: [],
  admin: [],
  hospitalization: [],
  ambulatory: [],
  oncology: [],
  doctor_register: [],
  director_doctor_register: [],
  admin_sectorial_crmo: []
}

const characters = [
  {
    label: 'Eletivo',
    value: 'elective'
  },
  {
    label: 'Urgência',
    value: 'urgency'
  }
]

const extraBloods = [
  {
    label: 'Sim',
    value: true
  },
  {
    label: 'Não',
    value: false
  }
]

const bloodTransfusion = [
  {
    label: 'Restrição a transfusão sanguínea',
    value: true
  },
  {
    label: 'Não tem restrição a transfusão sanguínea',
    value: false
  }
]
